import isEqual from 'lodash/isEqual';
import lowerCase from 'lodash/lowerCase';

export default class LocationUtil {
  public static isManualSecondaryAddress(
    caseLocation: ICaseLocation | ICaseLocationEditRequest | IXerceCaseLocationRequest,
    parcel: IParcel
  ) {
    let isManualSecondaryAddress;
    if (!caseLocation.isGISValidated) {
      isManualSecondaryAddress = true;
    } else if (caseLocation.isGISValidated && parcel && parcel.isManualSecondaryAddress) {
      isManualSecondaryAddress = true;
    } else if (caseLocation.isGISValidated && parcel && caseLocation.secUnitNum !== parcel.secUnitNum) {
      isManualSecondaryAddress = true;
    } else {
      isManualSecondaryAddress = false;
    }

    return isManualSecondaryAddress;
  }

  public getFullAddress(location: Partial<ILocation>): string {
    if (!location) {
      return '-';
    } else {
      const { city, state, zip, placeName, streetAddress } = location;
      let fullAddress = '';
      fullAddress += placeName && placeName.trim() !== '' ? `${placeName}, ` : '';
      fullAddress += streetAddress && streetAddress.trim() !== '' ? `${streetAddress}, ` : '';
      fullAddress += city && city.trim() !== '' ? `${city}, ` : '';
      fullAddress += state && state.trim() !== '' ? `${state} ` : '';
      fullAddress += zip && String(zip).trim() !== '' ? `${zip}` : '';

      return !fullAddress.trim().length ? '-' : fullAddress;
    }
  }

  public getFullAddressFromLocation(location: Partial<ILocation>, gisVersion: GISVersion = GISVersion.GIS_1_0): string {
    if (!location) {
      return '-';
    }
    const { city, state, zip, streetAddress, secUnitNum } = location;
    let fullAddress = '';
    if (gisVersion === GISVersion.GIS_2_0 || location.isGISValidated) {
      fullAddress +=
        streetAddress && !!streetAddress.trim() && secUnitNum && !!secUnitNum.trim()
          ? `${streetAddress} #${secUnitNum}, `
          : streetAddress && !!streetAddress.trim()
          ? `${streetAddress}, `
          : '';
    } else {
      fullAddress += streetAddress && streetAddress.trim() !== '' ? `${streetAddress}, ` : '';
    }
    fullAddress += city && city.trim() !== '' ? `${city}, ` : '';
    fullAddress += state && state.trim() !== '' ? `${state} ` : '';
    fullAddress += zip && zip.trim() !== '' ? `${zip}` : '';

    return !fullAddress.trim().length ? '-' : fullAddress;
  }

  public isManualSecondaryAddress(
    location: ICRMIssueLocationRequest,
    parcel: IParcel,
    gisVersion: GISVersion = GISVersion.GIS_1_0
  ): boolean {
    if (gisVersion === GISVersion.GIS_1_0) {
      return true;
    }
    if (!location.isGISValidated) {
      return true;
    } else if (location.isGISValidated && parcel && parcel.isManualSecondaryAddress) {
      return true;
    } else if (location.isGISValidated && parcel && location.secUnitNum !== parcel.secUnitNum) {
      return true;
    } else {
      return false;
    }
  }

  public getFieldsByType(types: LocationFieldType[], fields: ILocationFieldResponse[]) {
    return !!fields ? fields.filter((customField) => types.includes(customField.locationFieldType)) : [];
  }

  public getFlagHistoryStatus(history: IFlagHistoryResponse[]) {
    return history && !!history.length && history[0].isFlagged;
  }

  public getParcelOwnerInfo(key: string, parcel: IParcel) {
    return (parcel[key] && parcel[key].trim()) || '';
  }

  public getOwnerInfo(key: string, location: ICaseLocation | IXerceCaseLocationResponse | ICRMIssueLocationResponse) {
    const parcel: IParcel = location.parcel;
    if (parcel && parcel[key]) {
      return (parcel[key] && parcel[key].trim()) || '';
    } else {
      return (location[key] && location[key].trim()) || '';
    }
  }

  /**
   * A parcel is always serialised with isCDBGEligible false and null CDBG fields, and the CDBG
   * layer overwrites all of them only for coordinates it covers. So parcel.isCDBGEligible is a
   * verdict only when the parcel carries layer data; otherwise the value stored on the location
   * is the better answer.
   */
  public getCDBGEligibility(parcel: IParcel, storedEligibility: boolean): boolean {
    const hasCDBGLayerData =
      !!parcel && (parcel.cdbgCensusTract != null || parcel.cdbgBlockGroup != null || parcel.cdbgLowModPercent != null);

    return (hasCDBGLayerData ? parcel.isCDBGEligible : storedEligibility) ?? false;
  }

  public getFlagAddressRequest = (location: ICRMIssueLocationResponse): IFlagAddressRequest => {
    const flagHistory: IFlagHistoryResponse[] = location
      ? location.isEdited || (location && (location.flagHistory || []).length > 0)
        ? location.flagHistory
        : location && ((location.parcel && location.parcel.flagHistory) || []).length
        ? location.parcel.flagHistory
        : []
      : [];

    return {
      isFlagged: flagHistory.length > 0 ? flagHistory[0].isFlagged : false,
      reasonForFlagging: flagHistory.length > 0 ? flagHistory[0].reasonForFlagging : '',
    };
  };

  public isLocationFieldValueEmpty = (value: any, field: ILocationFieldResponse) => {
    if (value === 0) {
      return false;
    }

    return (
      !value ||
      (field.type !== CustomFieldType.NUMBER && field.type !== CustomFieldType.DATE && !String(value).trim().length)
    );
  };

  public isSameAddressGISValidation(location1: Partial<ILocation>, location2: Partial<ILocation>): boolean {
    const { city: city1, zip: zip1, streetAddress: streetAddress1, parcelFields: parcelFields1 } = location1;
    const { city: city2, zip: zip2, streetAddress: streetAddress2, parcelFields: parcelFields2 } = location2;

    if (streetAddress1 !== streetAddress2 || city1.toLowerCase() !== city2.toLowerCase() || zip1 !== zip2) {
      return false;
    } else if (!isEqual(parcelFields1, parcelFields2)) {
      return false;
    } else {
      return true;
    }
  }

  public static isSameAddress(location1: Partial<ILocation>, location2: Partial<ILocation>): boolean {
    const { city: city1, state: state1, zip: zip1, streetAddress: streetAddress1 } = location1;
    const { city: city2, state: state2, zip: zip2, streetAddress: streetAddress2 } = location2;

    if (
      lowerCase(streetAddress1) !== lowerCase(streetAddress2) ||
      lowerCase(city1) !== lowerCase(city2) ||
      lowerCase(state1) !== lowerCase(state2) ||
      lowerCase(zip1) !== lowerCase(zip2)
    ) {
      return false;
    } else {
      return true;
    }
  }
  public isSameStreetAddress(location1: Partial<ILocation>, location2: Partial<ILocation>): boolean {
    const { streetAddress: streetAddress1 } = location1;
    const { streetAddress: streetAddress2 } = location2;

    if (streetAddress1 !== streetAddress2) {
      return false;
    } else {
      return true;
    }
  }

  public static getPinCoordinates(lat, long, decimalPlaces = 5): Array<number> {
    const re = new RegExp(`^-?\\d+(?:\.\\d{0,${decimalPlaces || -1}})?`);

    return [lat.toString().match(re)[0], long.toString().match(re)[0]];
  }
}

export function getLocationPreferenceForUser(userProfile: IUserProfile) {
  const { isGISEnterprise, isGISDirect } = userProfile;
  const userLocationSetting = isGISDirect
    ? LocationFieldType.GIS_DIRECT
    : isGISEnterprise
    ? LocationFieldType.PARCEL_FILE
    : LocationFieldType.MANUAL;

  return userLocationSetting;
}
